import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { LogOut, Calendar as CalendarIcon, Image, Trash2, Upload, Edit, Ban, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Dialogs
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  
  // Gallery form
  const [galleryTitle, setGalleryTitle] = useState('');
  const [beforeImage, setBeforeImage] = useState('');
  const [afterImage, setAfterImage] = useState('');

  // Edit appointment
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editForm, setEditForm] = useState({
    service_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    date: '',
    time: '',
    status: ''
  });

  // Block slot form
  const [blockForm, setBlockForm] = useState({
    date: null,
    start_time: '',
    end_time: '',
    reason: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, []);

  // Helper function to parse date string safely (avoids timezone issues)
  const parseDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const loadData = async () => {
    const token = localStorage.getItem('admin_token');
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };

    try {
      const [appointmentsRes, galleryRes, servicesRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/appointments`, config),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/services`),
        axios.get(`${API}/admin/blocked-slots`, config)
      ]);
      setAppointments(appointmentsRes.data);
      setGallery(galleryRes.data);
      setServices(servicesRes.data);
      setBlockedSlots(blockedRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        toast.error('Sesión expirada');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${API}/admin/appointments/${appointmentId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Error al actualizar');
    }
  };

  const openEditDialog = (appointment) => {
    setEditingAppointment(appointment);
    setEditForm({
      service_id: appointment.service_id,
      client_name: appointment.client_name,
      client_email: appointment.client_email,
      client_phone: appointment.client_phone,
      date: appointment.date,
      time: appointment.time,
      status: appointment.status
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.put(
        `${API}/admin/appointments/${editingAppointment.id}/edit`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cita actualizada');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!confirm('¿Estás seguro de eliminar esta cita?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API}/admin/appointments/${appointmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cita eliminada');
      loadData();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleImageUpload = (e, setImage) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGallerySubmit = async (e) => {
    e.preventDefault();
    if (!galleryTitle || !beforeImage || !afterImage) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API}/admin/gallery`,
        { title: galleryTitle, before_image: beforeImage, after_image: afterImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Foto agregada exitosamente');
      setShowGalleryDialog(false);
      setGalleryTitle('');
      setBeforeImage('');
      setAfterImage('');
      loadData();
    } catch (error) {
      console.error('Error adding gallery item:', error);
      toast.error('Error al agregar foto');
    } finally {
      setLoading(false);
    }
  };

  const deleteGalleryItem = async (itemId) => {
    if (!confirm('¿Estás seguro de eliminar esta foto?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API}/admin/gallery/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Foto eliminada');
      loadData();
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    if (!blockForm.date || !blockForm.start_time || !blockForm.end_time || !blockForm.reason) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API}/admin/blocked-slots`,
        {
          date: format(blockForm.date, 'yyyy-MM-dd'),
          start_time: blockForm.start_time,
          end_time: blockForm.end_time,
          reason: blockForm.reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Horario bloqueado');
      setShowBlockDialog(false);
      setBlockForm({ date: null, start_time: '', end_time: '', reason: '' });
      loadData();
    } catch (error) {
      console.error('Error blocking slot:', error);
      toast.error(error.response?.data?.detail || 'Error al bloquear');
    } finally {
      setLoading(false);
    }
  };

  const deleteBlockedSlot = async (slotId) => {
    if (!confirm('¿Estás seguro de desbloquear este horario?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API}/admin/blocked-slots/${slotId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Horario desbloqueado');
      loadData();
    } catch (error) {
      console.error('Error deleting blocked slot:', error);
      toast.error('Error al desbloquear');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-300';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmado';
      case 'cancelled': return 'Cancelado';
      case 'completed': return 'Completado';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100">
      {/* Header */}
      <div className="bg-white border-b-2 border-pink-100 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Los Angeles Royale Beauty</h1>
            <p className="text-sm text-gray-600">Panel de Administración</p>
          </div>
          <Button 
            onClick={handleLogout} 
            variant="outline" 
            className="border-2 border-pink-200 hover:bg-pink-50"
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="appointments" className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 mb-8">
            <TabsTrigger value="appointments" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white" data-testid="appointments-tab">
              <CalendarIcon className="mr-2 h-4 w-4" />
              Citas
            </TabsTrigger>
            <TabsTrigger value="blocked" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white" data-testid="blocked-tab">
              <Ban className="mr-2 h-4 w-4" />
              Bloqueos
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white" data-testid="gallery-tab">
              <Image className="mr-2 h-4 w-4" />
              Galería
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="shadow-xl border-2 border-pink-100">
              <CardHeader>
                <CardTitle>Todas las Citas</CardTitle>
                <CardDescription>Gestiona las reservas de los clientes</CardDescription>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No hay citas registradas</p>
                ) : (
                  <div className="space-y-4" data-testid="appointments-list">
                    {appointments.map((apt, index) => (
                      <div 
                        key={apt.id} 
                        className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:shadow-md transition-shadow"
                        data-testid={`appointment-${index}`}
                      >
                        <div className="flex flex-wrap justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg" data-testid={`appointment-client-${index}`}>{apt.client_name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(apt.status)}`} data-testid={`appointment-status-${index}`}>
                                {getStatusText(apt.status)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1"><strong>Servicio:</strong> {apt.service_name} ({apt.duration_minutes} min)</p>
                            <p className="text-sm text-gray-600 mb-1"><strong>Fecha:</strong> {format(parseDate(apt.date), 'PPP', { locale: es })} - {apt.time}</p>
                            <p className="text-sm text-gray-600 mb-1"><strong>Email:</strong> {apt.client_email}</p>
                            <p className="text-sm text-gray-600"><strong>Teléfono:</strong> {apt.client_phone}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(apt)}
                              data-testid={`edit-btn-${index}`}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                            {apt.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => updateAppointmentStatus(apt.id, 'confirmed')}
                                className="bg-green-500 hover:bg-green-600 text-white"
                                data-testid={`confirm-btn-${index}`}
                              >
                                ✓ Confirmar
                              </Button>
                            )}
                            {apt.status === 'confirmed' && (
                              <Button
                                size="sm"
                                onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                                data-testid={`complete-btn-${index}`}
                              >
                                Completar
                              </Button>
                            )}
                            {apt.status !== 'cancelled' && apt.status !== 'completed' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => updateAppointmentStatus(apt.id, 'cancelled')}
                                data-testid={`cancel-btn-${index}`}
                              >
                                Cancelar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteAppointment(apt.id)}
                              data-testid={`delete-btn-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blocked Slots Tab */}
          <TabsContent value="blocked">
            <Card className="shadow-xl border-2 border-pink-100">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Horarios Bloqueados</CardTitle>
                    <CardDescription>Gestiona los bloqueos manuales de horarios</CardDescription>
                  </div>
                  <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-pink-500 hover:bg-pink-600" data-testid="add-block-btn">
                        <Ban className="mr-2 h-4 w-4" />
                        Bloquear Horario
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Bloquear Horario</DialogTitle>
                        <DialogDescription>Bloquea un rango de horas en una fecha específica</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleBlockSubmit} className="space-y-4">
                        <div>
                          <Label>Fecha</Label>
                          <Calendar
                            mode="single"
                            selected={blockForm.date}
                            onSelect={(date) => setBlockForm({...blockForm, date})}
                            disabled={{ dayOfWeek: [0], before: new Date() }}
                            locale={es}
                            className="rounded-lg border-2 border-pink-100 bg-white p-3"
                            data-testid="block-calendar"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="start_time">Hora Inicio</Label>
                            <Input
                              id="start_time"
                              type="time"
                              value={blockForm.start_time}
                              onChange={(e) => setBlockForm({...blockForm, start_time: e.target.value})}
                              required
                              data-testid="block-start-time"
                            />
                          </div>
                          <div>
                            <Label htmlFor="end_time">Hora Fin</Label>
                            <Input
                              id="end_time"
                              type="time"
                              value={blockForm.end_time}
                              onChange={(e) => setBlockForm({...blockForm, end_time: e.target.value})}
                              required
                              data-testid="block-end-time"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="reason">Motivo</Label>
                          <Input
                            id="reason"
                            value={blockForm.reason}
                            onChange={(e) => setBlockForm({...blockForm, reason: e.target.value})}
                            placeholder="Ej: Almuerzo, Día libre, etc."
                            required
                            data-testid="block-reason"
                          />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full" data-testid="submit-block-btn">
                          {loading ? 'Bloqueando...' : 'Bloquear Horario'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {blockedSlots.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No hay horarios bloqueados</p>
                ) : (
                  <div className="space-y-3" data-testid="blocked-slots-list">
                    {blockedSlots.map((slot, index) => (
                      <div 
                        key={slot.id}
                        className="p-4 rounded-lg border-2 border-orange-200 bg-orange-50 flex justify-between items-center"
                        data-testid={`blocked-slot-${index}`}
                      >
                        <div>
                          <p className="font-semibold text-gray-800">{format(parseDate(slot.date), 'PPP', { locale: es })}</p>
                          <div className="text-sm text-gray-600 flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>{slot.start_time} - {slot.end_time}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1"><strong>Motivo:</strong> {slot.reason}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteBlockedSlot(slot.id)}
                          data-testid={`unblock-btn-${index}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Desbloquear
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <Card className="shadow-xl border-2 border-pink-100">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Galería Antes/Después</CardTitle>
                    <CardDescription>Gestiona las fotos de transformaciones</CardDescription>
                  </div>
                  <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-pink-500 hover:bg-pink-600" data-testid="add-gallery-btn">
                        <Upload className="mr-2 h-4 w-4" />
                        Agregar Foto
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Agregar Foto Antes/Después</DialogTitle>
                        <DialogDescription>Sube las imágenes de transformación</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleGallerySubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="title">Título</Label>
                          <Input
                            id="title"
                            value={galleryTitle}
                            onChange={(e) => setGalleryTitle(e.target.value)}
                            placeholder="Ej: Tratamiento Facial"
                            required
                            data-testid="gallery-title-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="before">Foto Antes</Label>
                          <Input
                            id="before"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, setBeforeImage)}
                            required
                            data-testid="before-image-input"
                          />
                          {beforeImage && (
                            <img src={beforeImage} alt="Preview" className="mt-2 h-32 w-32 object-cover rounded-lg" />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="after">Foto Después</Label>
                          <Input
                            id="after"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, setAfterImage)}
                            required
                            data-testid="after-image-input"
                          />
                          {afterImage && (
                            <img src={afterImage} alt="Preview" className="mt-2 h-32 w-32 object-cover rounded-lg" />
                          )}
                        </div>
                        <Button type="submit" disabled={loading} className="w-full" data-testid="submit-gallery-btn">
                          {loading ? 'Agregando...' : 'Agregar a Galería'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {gallery.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No hay fotos en la galería</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6" data-testid="gallery-list">
                    {gallery.map((item, index) => (
                      <div key={item.id} className="border-2 border-gray-200 rounded-xl p-4 bg-white" data-testid={`gallery-item-${index}`}>
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-lg">{item.title}</h3>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteGalleryItem(item.id)}
                            data-testid={`delete-gallery-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Antes</p>
                            <img src={item.before_image} alt="Antes" className="w-full h-40 object-cover rounded-lg" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Después</p>
                            <img src={item.after_image} alt="Después" className="w-full h-40 object-cover rounded-lg" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Cita</DialogTitle>
            <DialogDescription>Modifica los detalles de la cita</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_service">Servicio</Label>
              <Select
                value={editForm.service_id}
                onValueChange={(value) => setEditForm({...editForm, service_id: value})}
              >
                <SelectTrigger data-testid="edit-service-select">
                  <SelectValue placeholder="Selecciona servicio" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} ({service.duration_minutes} min)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_date">Fecha</Label>
                <Input
                  id="edit_date"
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                  data-testid="edit-date-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_time">Hora</Label>
                <Input
                  id="edit_time"
                  type="time"
                  value={editForm.time}
                  onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                  data-testid="edit-time-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_name">Nombre Cliente</Label>
              <Input
                id="edit_name"
                value={editForm.client_name}
                onChange={(e) => setEditForm({...editForm, client_name: e.target.value})}
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={editForm.client_email}
                onChange={(e) => setEditForm({...editForm, client_email: e.target.value})}
                data-testid="edit-email-input"
              />
            </div>
            <div>
              <Label htmlFor="edit_phone">Teléfono</Label>
              <Input
                id="edit_phone"
                value={editForm.client_phone}
                onChange={(e) => setEditForm({...editForm, client_phone: e.target.value})}
                data-testid="edit-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="edit_status">Estado</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({...editForm, status: value})}
              >
                <SelectTrigger data-testid="edit-status-select">
                  <SelectValue placeholder="Selecciona estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="confirmed">Confirmado</SelectItem>
                  <SelectItem value="completed">Completado</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="submit-edit-btn">
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;